import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getDailyProgressTodayStatus, sendProgressReminders } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Clock,
  CheckCircle,
  AlertTriangle,
  XCircle,
  RefreshCw,
  FileText,
  MapPin,
  User,
  Bell,
  ChevronRight,
  Shield,
  Briefcase,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';

const DailyProgressWidget = ({ compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, missing, on_time, late
  const [sendingReminders, setSendingReminders] = useState(false);

  useEffect(() => {
    fetchStatus();
    // Refresh every 5 minutes
    const interval = setInterval(fetchStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await getDailyProgressTodayStatus();
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch daily progress status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const response = await sendProgressReminders();
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  const getIndicatorIcon = (indicator, updateCount = 0) => {
    // Multiple updates get special treatment
    if (updateCount > 1) {
      return (
        <div className="flex items-center gap-1">
          <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
            <span className="text-white text-xs font-bold">{updateCount}</span>
          </div>
        </div>
      );
    }
    
    switch (indicator) {
      case 'green':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'yellow':
        return <Clock className="w-5 h-5 text-amber-500" />;
      case 'red':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'blue':
        return <CheckCircle className="w-5 h-5 text-blue-500" />;
      default:
        return <Clock className="w-5 h-5 text-slate-400" />;
    }
  };

  const getIndicatorBg = (indicator, updateCount = 0) => {
    // Multiple updates get blue background
    if (updateCount > 1) {
      return 'bg-blue-50 border-blue-200 hover:bg-blue-100';
    }
    
    switch (indicator) {
      case 'green':
        return 'bg-green-50 border-green-200 hover:bg-green-100';
      case 'yellow':
        return 'bg-amber-50 border-amber-200 hover:bg-amber-100';
      case 'red':
        return 'bg-red-50 border-red-200 hover:bg-red-100';
      case 'blue':
        return 'bg-blue-50 border-blue-200 hover:bg-blue-100';
      default:
        return 'bg-slate-50 border-slate-200';
    }
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '-';
    return new Date(isoStr).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const filteredWorkOrders = data?.work_orders?.filter(wo => {
    if (filter === 'all') return true;
    if (filter === 'missing') return !wo.report_submitted;
    if (filter === 'on_time') return wo.status === 'on_time';
    if (filter === 'late') return wo.status === 'late';
    if (filter === 'multiple') return wo.today_update_count > 1;
    return true;
  }) || [];

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent className="text-center py-8 text-slate-500">
          Failed to load daily progress status
        </CardContent>
      </Card>
    );
  }

  // Compact version for sidebar/small widgets
  if (compact) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center justify-between">
            <span className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-600" />
              Daily Progress
            </span>
            <Badge variant="outline" className="text-xs">
              Today
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="p-2 bg-green-50 rounded-lg">
              <p className="text-xl font-bold text-green-600">{data.summary.on_time}</p>
              <p className="text-xs text-green-700">On Time</p>
            </div>
            <div className="p-2 bg-amber-50 rounded-lg">
              <p className="text-xl font-bold text-amber-600">{data.summary.late}</p>
              <p className="text-xs text-amber-700">Late</p>
            </div>
            <div className="p-2 bg-red-50 rounded-lg">
              <p className="text-xl font-bold text-red-600">{data.summary.missing}</p>
              <p className="text-xs text-red-700">Missing</p>
            </div>
          </div>
          {data.summary.missing > 0 && data.is_past_deadline && (
            <div className="mt-3 p-2 bg-red-100 rounded-lg text-center">
              <p className="text-xs text-red-700 font-medium">
                ⚠️ {data.summary.missing} report(s) missing past deadline
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Full widget
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Daily Progress Status
            <Badge variant="outline">Today</Badge>
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchStatus}>
              <RefreshCw className="w-4 h-4" />
            </Button>
            {data.summary.missing > 0 && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleSendReminders}
                disabled={sendingReminders}
                className="text-amber-600 border-amber-300 hover:bg-amber-50"
              >
                <Bell className="w-4 h-4 mr-1" />
                {sendingReminders ? 'Sending...' : 'Send Reminders'}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Summary Stats */}
        <div className="grid grid-cols-5 gap-2 mb-4">
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <p className="text-xl font-bold text-slate-700">{data.summary.total_active}</p>
            <p className="text-xs text-slate-500">Active</p>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <p className="text-xl font-bold text-green-600">{data.summary.on_time}</p>
            <p className="text-xs text-green-700">🟢 On Time</p>
          </div>
          <div className="text-center p-3 bg-amber-50 rounded-lg">
            <p className="text-xl font-bold text-amber-600">{data.summary.late}</p>
            <p className="text-xs text-amber-700">🟡 Late</p>
          </div>
          <div className="text-center p-3 bg-red-50 rounded-lg">
            <p className="text-xl font-bold text-red-600">{data.summary.missing}</p>
            <p className="text-xs text-red-700">🔴 Missing</p>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <p className="text-xl font-bold text-blue-600">{data.summary.multiple_updates || 0}</p>
            <p className="text-xs text-blue-700">🔷 Multi</p>
          </div>
        </div>

        {/* Deadline Status */}
        <div className={`p-3 rounded-lg mb-4 ${data.is_past_deadline ? 'bg-red-100 border border-red-200' : 'bg-blue-50 border border-blue-200'}`}>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Deadline: 1:00 PM
            </span>
            <Badge className={data.is_past_deadline ? 'bg-red-500' : 'bg-blue-500'}>
              {data.is_past_deadline ? 'Past Deadline' : 'Before Deadline'}
            </Badge>
          </div>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-slate-400" />
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Work Orders</SelectItem>
              <SelectItem value="missing">🔴 Missing Reports</SelectItem>
              <SelectItem value="on_time">🟢 On Time</SelectItem>
              <SelectItem value="late">🟡 Late</SelectItem>
              <SelectItem value="multiple">🔷 Multiple Updates</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-slate-500">
            {filteredWorkOrders.length} result(s)
          </span>
        </div>

        {/* Work Order List */}
        <ScrollArea className="h-[300px]">
          <div className="space-y-2">
            {filteredWorkOrders.map((wo) => (
              <Link
                key={wo.work_order_id}
                to={`/work-orders/${wo.work_order_id}`}
                className={`block p-3 rounded-lg border transition-colors ${getIndicatorBg(wo.indicator, wo.today_update_count)}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getIndicatorIcon(wo.indicator, wo.today_update_count)}
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">{wo.wo_number}</p>
                        {wo.today_update_count > 1 && (
                          <Badge className="bg-blue-500 text-white text-xs px-1.5 py-0">
                            🔷 x{wo.today_update_count}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <MapPin className="w-3 h-3" />
                        <span className="truncate max-w-[150px]">{wo.location}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2">
                      {wo.safety_flag && (
                        <Shield className="w-4 h-4 text-red-500" title="Safety concern" />
                      )}
                      {wo.union_flag && (
                        <Briefcase className="w-4 h-4 text-amber-500" title="Union concern" />
                      )}
                      <span className="font-bold text-lg text-blue-600">
                        {wo.percent_complete}%
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">
                      {wo.report_submitted ? (
                        <>Submitted {formatTime(wo.submitted_time)}</>
                      ) : (
                        <span className="text-red-600">Not submitted</span>
                      )}
                    </p>
                  </div>
                </div>
                {wo.crew_leader_name && (
                  <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {wo.crew_leader_name}
                    </span>
                    {wo.time_label && (
                      <Badge variant="outline" className="text-xs">
                        {wo.time_label}
                      </Badge>
                    )}
                  </div>
                )}
              </Link>
            ))}
            
            {filteredWorkOrders.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                No work orders match the filter
              </div>
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default DailyProgressWidget;
